import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { duplicateLeadMergeableKey, leadKey, leadMergeableKey } from 'utils/constants/queryKey'
import { AppQueryOptions, useAppQueryMap, useAppQuery } from 'hooks/use-app-query'
import { useAppInfiniteQueryMap } from 'hooks/use-app-infinite-query'

import {
  getLeadDetailApi,
  getLeadListApi,
  checkLeadExistApi,
  getLeadStatistics,
  getMergeableLeadListApi,
  mergeLeads,
  getMergeableDuplicateLeadListApi,
  updateLeadInfo,
  searchLeadListApi,
} from 'services/lead'
import { GetLeadSearchResponse, GetLeadsPayload, GetMergeableLeadsPayload, MergeLeads } from 'services/types/lead'
import {
  mapLeadDetailResponse,
  mapGetLeadResponse,
  mapGetMergeableLeadListResponse,
  mapSearchLeadResponse,
} from 'store/lead'
import useAppMutation from 'hooks/use-app-mutation'
import { useAppSelector } from 'store'
import { LeadInfoSearch } from 'store/types/lead'

const useLeadList = (payload: GetLeadsPayload, options?: AppQueryOptions) => {
  const branchId = useAppSelector((state) => state.user.selectedBranch?.branchId) ?? ''
  return useAppQueryMap(
    [{ branchId }, ...leadKey.list(payload)],
    () => getLeadListApi(payload),
    mapGetLeadResponse,
    options,
  )
}

export const useInfiniteSearchLeadList = (payload: GetLeadsPayload, options?: AppQueryOptions) => {
  const branchId = useAppSelector((state) => state.user.selectedBranch?.branchId) ?? ''

  const queryResult = useAppInfiniteQueryMap<GetLeadSearchResponse, { count: string; data: LeadInfoSearch[] }>(
    [{ branchId }, ...leadKey.list(payload)],
    ({ pageParam = 0 }) => searchLeadListApi({ ...payload, page: pageParam || 0 }),
    mapSearchLeadResponse,
    {
      ...options,
    },
  )

  const computedList = useMemo(() => queryResult.data?.pages.flatMap((page) => page?.data || []) || [], [
    queryResult.data,
  ])

  const totalCount = useMemo(
    () => Math.max(...(queryResult.data?.pages?.map((page) => Number(page?.count) || 0) || [0])),
    [queryResult.data],
  )

  return {
    ...queryResult,
    list: computedList || [],
    totalCount: totalCount || 0,
  }
}

const useMergeableLeadList = (payload: GetMergeableLeadsPayload, options?: AppQueryOptions) => {
  const branchId = useAppSelector((state) => state.user.selectedBranch?.branchId) ?? ''
  return useAppQueryMap(
    [{ branchId }, ...leadMergeableKey.list(payload)],
    () =>
      getMergeableLeadListApi({
        master_lead_id: payload.leadId,
        page: payload?.pageNumber,
        per_page: payload?.pageSize,
        query: payload?.search,
        sort_by: payload?.sorter,
        duplicate_lead_ids: payload?.duplicateLeadIds,
      }),
    mapGetMergeableLeadListResponse,
    options,
  )
}

const useMergeableDuplicateLeadList = (payload: GetMergeableLeadsPayload, options?: AppQueryOptions) => {
  const branchId = useAppSelector((state) => state.user.selectedBranch?.branchId) ?? ''
  return useAppQuery(
    [{ branchId }, ...duplicateLeadMergeableKey.list(payload)],
    () =>
      getMergeableDuplicateLeadListApi({
        lead_id: payload.leadId,
      }),
    options,
  )
}

const useInvalidateMergeableLeadList = () => {
  const branchId = useAppSelector((state) => state.user.selectedBranch?.branchId) ?? ''
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries([{ branchId }, ...duplicateLeadMergeableKey.lists()])
}

const useMergeLead = () => {
  const { mutateAsync, isLoading, isError, error } = useAppMutation([], mergeLeads)

  return {
    mergeLead: (payload: MergeLeads.Payload) => mutateAsync(payload),
    isLeadMerging: isLoading,
    isLeadMergingErrored: isError,
    leadMergingError: error,
  }
}

const useQueryDetail = (id?: string, options?: AppQueryOptions) =>
  useAppQueryMap(leadKey.detail(id), () => getLeadDetailApi(id ?? ''), mapLeadDetailResponse, {
    ...options,
    enabled: !!options?.enabled && !!id,
  })

const useLeadExist = () => {
  const branchId = useAppSelector((state) => state.user.selectedBranch?.branchId) ?? ''
  return useAppQuery([{ branchId }, ...leadKey.leadExist()], () => checkLeadExistApi())
}

const useLeadStat = () => {
  const branchId = useAppSelector((state) => state.user.selectedBranch?.branchId) ?? ''
  return useAppQuery([{ branchId }, ...leadKey.leadStat()], () => getLeadStatistics())
}

const useInvalidateItem = (id: string) => {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries(leadKey.data(id))
    queryClient.invalidateQueries(leadKey.detail(id))
  }
}

const useInvalidateList = () => {
  const branchId = useAppSelector((state) => state.user.selectedBranch?.branchId) ?? ''
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries([{ branchId }, ...leadKey.lists()])
}

export const useUpdateLeadInfo = (props: { leadId: string | undefined } | undefined) => {
  const invalidate = useInvalidateItem(props?.leadId ?? '')
  const mutation = useAppMutation([], updateLeadInfo, {
    onSuccess: () => {
      invalidate()
    },
  })
  return mutation
}

export default {
  useLeadList,
  useLeadExist,
  useLeadStat,
  useQueryDetail,
  useInvalidateItem,
  useInvalidateList,
  useMergeableDuplicateLeadList,
  useInvalidateMergeableLeadList,
  useMergeLead,
  useMergeableLeadList,
  useUpdateLeadInfo,
}
